import {noop} from '@benchling/js-utils';
import _ from 'underscore';

import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications, callWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import * as EventConstants from 'constants/EventConstants';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import {GridSummary} from 'constants/SampleConstants';
import {SampleControlParams} from 'constants/SampleStatusConstants';
import AppDispatcher from 'lib/Dispatcher';
import notifications, {Response} from 'notifications';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import {CreateGridParams} from 'samples/GridDAO';
import getCreateGridParams, {CreateGridPaneState} from 'samples/grids/getCreateGridParams';
import {GENERIC_SHORT_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import APIUtil from 'util/APIUtil';

import * as TransferDAO from './TransferDAO';

type CalculateConcentrationsResponse = TransferDAO.CalculateConcentrationsResponse;
type ContainerInvalidatableResponse = TransferDAO.ContainerInvalidatableResponse;
type ExistingGridParams = TransferDAO.ExistingGridParams;
type TransferContainersResponse = TransferDAO.TransferContainersResponse;

interface TransferGridsParams {
  containerContents: Array<TransferDAO.ContainerContentParam>;
  inputGridId: string;
  shouldTransferAllQuantities: boolean;
  transferQuantity: LegacyMeasurement;
  folderId?: string;
  createGridParams?: CreateGridParams;
  existingGridParams?: ExistingGridParams;
  onStart?: () => void;
  onFinish?: (errorMessage?: string) => void;
  dataModsHeader?: DataModificationsHeader;
}

function transferGridsWithDataMods(transferGridParams: TransferGridsParams): void {
  const apiIds = [transferGridParams.inputGridId];
  if (transferGridParams.existingGridParams?.outputGridId) {
    apiIds.push(transferGridParams.existingGridParams.outputGridId);
  }

  callWithDataModifications({
    changeSummary: getInventoryRegistryChangeSummary({
      apiIds,
      action: InventoryRegistryDataModAction.TRANSFER_PLATE,
    }),
    onPerformAction: (dataModsHeader?) => {
      transferGrids({...transferGridParams, dataModsHeader});
    },
    apiIds,
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.TRANSFER_PLATE,
      numModifiedTotalItems: apiIds.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

function transferGrids({
  containerContents,
  inputGridId,
  shouldTransferAllQuantities,
  transferQuantity,
  folderId,
  createGridParams,
  existingGridParams,
  onStart = noop,
  onFinish = noop,
  dataModsHeader,
}: TransferGridsParams): void {
  onStart();
  TransferDAO.transferGrids(
    containerContents,
    inputGridId,
    shouldTransferAllQuantities,
    transferQuantity,
    folderId,
    createGridParams,
    existingGridParams,
    dataModsHeader
  )
    .done(
      ({
        invalidatableBatchIds,
        invalidatableContainedItemIds,
        invalidatableStorableIds,
      }: ContainerInvalidatableResponse) => {
        notifications.success('Plate transfer completed');
        onFinish();
        AppDispatcher.dispatch({
          actionType: EventConstants.CONTAINERS_TRANSFER_COMPLETED,
          invalidatableBatchIds,
          invalidatableContainedItemIds,
          invalidatableStorableIds,
        });
      }
    )
    .fail((response) => {
      const errorMessage = notifications.getFriendlyError(response);
      if (!errorMessage) {
        // If errorMessage is null, then the error was not "friendly", so we should notify the user that we
        // encountered an unexpected error.
        notifications.notifyError(response);
      }
      onFinish(errorMessage || GENERIC_SHORT_ERROR_MESSAGE);
    });
}

interface TransferPlatesQuantityPaneState {
  shouldTransferAllQuantities: boolean;
  transferQuantity: LegacyMeasurement;
}

interface ConfirmConcentrationsPaneState {
  containerContents: Array<TransferDAO.ContainerContentParam>;
}

interface ExistingPlatePaneState {
  selectedGrid: GridSummary;
}

export function transferToNewGrid(
  inputGridId: string,
  createGridPaneState: CreateGridPaneState,
  transferPlatesQuantityPaneState: TransferPlatesQuantityPaneState,
  confirmConcentrationsPaneState: ConfirmConcentrationsPaneState,
  onStart?: () => void,
  onFinish?: (errorMessage?: string) => void
): void {
  const folderId = createGridPaneState.registry.rootFolder.api_identifier;
  const createGridParams = getCreateGridParams(createGridPaneState);
  const {shouldTransferAllQuantities, transferQuantity} = transferPlatesQuantityPaneState;
  const {containerContents} = confirmConcentrationsPaneState;
  transferGridsWithDataMods({
    containerContents,
    inputGridId,
    shouldTransferAllQuantities,
    transferQuantity,
    folderId,
    createGridParams,
    onStart,
    onFinish,
  });
}

export function transferToExistingPlate(
  inputGridId: string,
  existingPlatePaneState: ExistingPlatePaneState,
  transferPlatesQuantityPaneState: TransferPlatesQuantityPaneState,
  confirmConcentrationsPaneState: ConfirmConcentrationsPaneState,
  onStart?: () => void,
  onFinish?: (errorMessage?: string) => void
): void {
  const {selectedGrid} = existingPlatePaneState;
  const {shouldTransferAllQuantities, transferQuantity} = transferPlatesQuantityPaneState;
  const {containerContents} = confirmConcentrationsPaneState;
  transferGridsWithDataMods({
    containerContents,
    inputGridId,
    shouldTransferAllQuantities,
    transferQuantity,
    existingGridParams: {outputGridId: selectedGrid.id},
    onStart,
    onFinish,
  });
}

function undoTransfers(
  undoToken: string,
  invalidatableBatchIds: Array<string>,
  invalidatableContainedItemIds: Array<string>,
  invalidatableStorableIds: Array<string>
): void {
  TransferDAO.undoTransfers(undoToken)
    .done(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.CONTAINERS_TRANSFER_UNDO_COMPLETED,
        invalidatableBatchIds,
        invalidatableContainedItemIds,
        invalidatableStorableIds,
      });
      notifications.success('Transfer was undone.');
    })
    .fail(notifications.notifyError);
}

function dispatchForTransferCompleted(
  {
    undoToken,
    invalidatableBatchIds,
    invalidatableContainedItemIds,
    invalidatableStorableIds,
  }: TransferContainersResponse,
  onFinish: () => void
): void {
  onFinish();
  AppDispatcher.dispatch({
    actionType: EventConstants.CONTAINERS_TRANSFER_COMPLETED,
    invalidatableBatchIds,
    invalidatableContainedItemIds,
    invalidatableStorableIds,
  });
  notifications.success('Successfully performed transfer.', {
    duration: 10 * 60,
    actionText: 'Undo',
    onActionClick: () => {
      undoTransfers(
        undoToken,
        invalidatableBatchIds,
        invalidatableContainedItemIds,
        invalidatableStorableIds
      );
    },
  });
}

function dispatchForTransferFailed(response: Response, onFinish: (errorMessage: string) => void): void {
  const errorMessage = notifications.getFriendlyError(response);
  if (!errorMessage) {
    // If errorMessage is null, then the error was not "friendly", so we should notify the user that we
    // encountered an unexpected error.
    notifications.notifyError(response);
  }
  onFinish(errorMessage || GENERIC_SHORT_ERROR_MESSAGE);
}

export async function calculateConcentrationsAndDoTransfersWithDataMods({
  containerTransfers,
  inputContainableConcentration,
  outputSampleControls,
  onStart = noop,
  onFinish = noop,
}: {
  containerTransfers: Array<TransferDAO.ContainerTransferParam>;
  inputContainableConcentration: LegacyMeasurement | null;
  outputSampleControls: SampleControlParams;
  onStart?: () => void;
  onFinish?: (transferCancelled: boolean, errorMessage?: string) => void;
}): Promise<void> {
  onStart();
  const apiIds = new Set(
    containerTransfers.flatMap(({inputSampleId, outputContainer}) =>
      inputSampleId != null && APIUtil.isContainer(inputSampleId)
        ? [inputSampleId, outputContainer.id]
        : [outputContainer.id]
    )
  );
  const apiIdsList = [...apiIds];
  const changeSummary = getInventoryRegistryChangeSummary({
    apiIds: apiIdsList,
    action: InventoryRegistryDataModAction.TRANSFER,
    containersMayBeWells: true,
  });
  await TransferDAO.calculateConcentrations(containerTransfers, true, inputContainableConcentration)
    .then(async ({containerContents}: CalculateConcentrationsResponse) => {
      await callAsyncWithDataModifications({
        changeSummary,
        onPerformAction: (dataModsHeader?) => {
          TransferDAO.transferContainers(
            containerTransfers,
            containerContents,
            outputSampleControls,
            dataModsHeader
          )
            .done((resp: TransferContainersResponse) => {
              dispatchForTransferCompleted(resp, () => {
                onFinish(false);
              });
            })
            .fail((resp) => {
              dispatchForTransferFailed(resp, (errorMessage) => {
                onFinish(false, errorMessage);
              });
            });
        },
        onModalCancel: () => {
          onFinish(true);
        },
        apiIds: apiIdsList,
        analyticsParams: {
          module: 'SAMPLES',
          actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
          modifiedAction: InventoryRegistryDataModAction.TRANSFER,
          numModifiedTotalItems: apiIdsList.length,
          entryId: null,
        },
        useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
      });
    })
    .catch((resp: Response) => {
      dispatchForCalculateConcentrationsFailed(resp, (errorMessage) => {
        onFinish(false, errorMessage);
      });
    });
}

export async function doTransfersWithDataMods(
  containerTransfers: Array<TransferDAO.ContainerTransferParam>,
  containerContents: Array<TransferDAO.ContainerContentParam>,
  outputSampleControls: SampleControlParams,
  onStart: () => void = noop,
  onFinish: (errorMessage?: string) => void = noop
): Promise<void> {
  const apiIds = new Set([
    ...containerTransfers.flatMap(({inputSampleId, outputContainer}) =>
      inputSampleId != null && APIUtil.isContainer(inputSampleId)
        ? [inputSampleId, outputContainer.id]
        : [outputContainer.id]
    ),
    ...containerContents.map(({containerId}) => containerId),
  ]);
  const apiIdsList = [...apiIds];
  const changeSummary = getInventoryRegistryChangeSummary({
    apiIds: apiIdsList,
    action: InventoryRegistryDataModAction.TRANSFER,
    containersMayBeWells: true,
  });

  await callAsyncWithDataModifications({
    changeSummary,
    onPerformAction: (dataModsHeader?) => {
      doTransfers(
        containerTransfers,
        containerContents,
        outputSampleControls,
        onStart,
        onFinish,
        dataModsHeader
      );
    },
    apiIds: apiIdsList,
    analyticsParams: {
      module: 'SAMPLES',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.TRANSFER,
      numModifiedTotalItems: apiIdsList.length,
      entryId: null,
    },
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

export function doTransfers(
  containerTransfers: Array<TransferDAO.ContainerTransferParam>,
  containerContents: Array<TransferDAO.ContainerContentParam>,
  outputSampleControls: SampleControlParams,
  onStart: () => void = noop,
  onFinish: (errorMessage?: string) => void = noop,
  dataModsHeader?: DataModificationsHeader
): void {
  onStart();
  TransferDAO.transferContainers(containerTransfers, containerContents, outputSampleControls, dataModsHeader)
    .done((resp: TransferContainersResponse) => {
      dispatchForTransferCompleted(resp, onFinish);
    })
    .fail((resp) => {
      dispatchForTransferFailed(resp, onFinish);
    });
}

export type OnCompleteCalculateConcentrationsCallback = (transferParams: {
  containerTransfers: CalculateConcentrationsResponse['containerTransfers'];
  containerContents: CalculateConcentrationsResponse['containerContents'];
}) => void;

/*
 * Dispatch when calculate concentrations completes.
 * @param onComplete Pass transfers & batches to this callback instead of dispatching to store.
 */
function dispatchForCalculateConcentrationsCompleted(
  {batches, containedBioentities, containerContents, containerTransfers}: CalculateConcentrationsResponse,
  onComplete: OnCompleteCalculateConcentrationsCallback
): void {
  getStore().dispatch(ModelActions.filesFetchCompleted(_.indexBy(containedBioentities, 'id')));
  AppDispatcher.dispatch({
    actionType: EventConstants.BATCHES_BULK_FETCH_COMPLETED,
    batchesById: _.indexBy(batches, 'id'),
  });
  onComplete({containerTransfers, containerContents});
}

/*
 * Dispatch when calculate concentrations fails.
 * @param onFailure Pass error to this callback instead of dispatching to store.
 */
function dispatchForCalculateConcentrationsFailed(
  response: Response,
  onFailure: (errorMessage: string) => void
): void {
  const errorMessage = notifications.getFriendlyError(response);
  if (!errorMessage) {
    // If errorMessage is null, then the error was not "friendly", so we should notify the user that we
    // encountered an unexpected error.
    notifications.notifyError(response);
  }
  onFailure(errorMessage || GENERIC_SHORT_ERROR_MESSAGE);
}

export function calculateConcentrations({
  containerTransfers,
  shouldCalculateConcentrations,
  inputContainableConcentration,
  onStart = noop,
  onComplete = noop,
  onFailure = noop,
}: {
  containerTransfers: Array<TransferDAO.ContainerTransferParam>;
  shouldCalculateConcentrations: boolean;
  inputContainableConcentration: LegacyMeasurement | null;
  onStart?: () => void;
  onComplete?: OnCompleteCalculateConcentrationsCallback;
  onFailure?: (errorMessage: string) => void;
}): void {
  onStart();
  TransferDAO.calculateConcentrations(
    containerTransfers,
    shouldCalculateConcentrations,
    inputContainableConcentration
  )
    .then((response: CalculateConcentrationsResponse) => {
      dispatchForCalculateConcentrationsCompleted(response, onComplete);
    })
    .catch((response: Response) => {
      dispatchForCalculateConcentrationsFailed(response, onFailure);
    });
}

export function calculateConcentrationsGridToGrid(
  inputGridId: string,
  outputGridId: string,
  shouldTransferAllQuantities: boolean,
  transferQuantity: LegacyMeasurement,
  onComplete: OnCompleteCalculateConcentrationsCallback = noop,
  onFailure: (errorMessage: string) => void = noop
): void {
  TransferDAO.calculateConcentrationsGridToGrid(
    inputGridId,
    outputGridId,
    shouldTransferAllQuantities,
    transferQuantity
  )
    .done((response: CalculateConcentrationsResponse) => {
      dispatchForCalculateConcentrationsCompleted(response, onComplete);
    })
    .fail((response) => {
      dispatchForCalculateConcentrationsFailed(response, onFailure);
    });
}
